// TaskManagementHOD.jsx - HOD Dashboard - Department Specific View
// Shows only data for the HOD's own department
// Enhanced: HOD can view staff daily updates, progress reports, and remarks
// Fixed: HOD can assign tasks to self, has full action capabilities

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Grid, Paper, Typography, Button, CircularProgress, 
  useMediaQuery, IconButton, Chip, Dialog, DialogTitle, 
  DialogContent, DialogActions, TextField, MenuItem, FormControl,
  InputLabel, Select, Snackbar, Alert, Avatar, Tooltip, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Pagination, InputAdornment, Card, CardContent, Divider, Badge,
  List, ListItem, ListItemAvatar, ListItemButton, LinearProgress,
  ListItemText, SpeedDialIcon, SpeedDial, Fade, Backdrop, SpeedDialAction,
  Checkbox, FormControlLabel, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  Dashboard as DashboardIcon, Assignment, Person, PersonAdd,
  CalendarToday, CheckCircle, Cancel, Refresh, Search, Visibility,
  Close, Add, Delete, Save, Comment, Timeline as TimelineIcon,
  Warning, Update, TaskAlt, Send, InsertComment, HowToReg,
  EventNote, TrendingUp, Notifications, DoneAll, BugReport,
  Flag, Warning as WarningIcon, BusinessCenter, Group,
  ExpandMore, History, RateReview
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { format, parseISO, addDays, differenceInDays, startOfMonth, endOfMonth, isSameDay } from 'date-fns';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';

import Sidebar from '../../components/sidebars/sidebar_hod';
import TopNav from '../../components/top_bars/TopNav_auditor';

// Styled Components
const PriorityBadge = styled(Chip)(({ theme, priority }) => ({
  backgroundColor: priority === 'high' ? theme.palette.error.main :
                  priority === 'medium' ? theme.palette.warning.main :
                  theme.palette.success.main,
  color: '#fff',
  fontWeight: 'bold',
  fontSize: '0.7rem',
  height: 24,
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const colors = {
    pending: '#ff9800',
    in_progress: '#2196f3',
    completed: '#4caf50',
    cancelled: '#9e9e9e',
    overdue: '#f44336',
    delegated: '#9c27b0',
  };
  return {
    backgroundColor: colors[status] || '#999',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '0.7rem',
    height: 24,
  };
});

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  transition: 'transform 0.2s, box-shadow 0.2s',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const CalendarDayCell = styled(Paper)(({ theme, isCurrentMonth, hasEvents, isToday }) => ({
  minHeight: 100,
  padding: theme.spacing(1),
  backgroundColor: isCurrentMonth ? '#fff' : '#fafafa',
  border: hasEvents ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
  position: 'relative',
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'scale(1.02)',
    zIndex: 1,
  },
  ...(isToday && {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    border: `2px solid ${theme.palette.primary.main}`,
  }),
}));

const TaskManagementHOD = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  
  // State for data
  const [departmentTasks, setDepartmentTasks] = useState([]);
  const [staffTasks, setStaffTasks] = useState([]);
  const [departmentStaff, setDepartmentStaff] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [stats, setStats] = useState({
    department_tasks: { total: 0, pending: 0, in_progress: 0, completed: 0, overdue: 0, avg_completion: 0 },
    staff_tasks: { total: 0, pending: 0, in_progress: 0, completed: 0, overdue: 0, avg_completion: 0, total_estimated_hours: 0, completed_hours: 0 },
    due_soon: 0,
    completed_this_week: 0,
  });
  const [departmentStats, setDepartmentStats] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [departmentPagination, setDepartmentPagination] = useState({ current_page: 1, per_page: 10, total: 0, total_pages: 0 });
  const [staffPagination, setStaffPagination] = useState({ current_page: 1, per_page: 10, total: 0, total_pages: 0 });
  
  // Calendar view state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [calendarEventDialogOpen, setCalendarEventDialogOpen] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  
  // Daily updates dialog state
  const [dailyUpdatesDialogOpen, setDailyUpdatesDialogOpen] = useState(false);
  const [selectedTaskUpdates, setSelectedTaskUpdates] = useState([]);
  const [selectedTaskForUpdates, setSelectedTaskForUpdates] = useState(null);
  
  // State for departments
  const [departments, setDepartments] = useState([]);
  const [currentUser, setCurrentUser] = useState({
    phone: '',
    name: '',
    department: '',
    email: '',
    user_type: '',
  });
  
  // State for staff list (only department staff for HOD, including HOD themselves for self-assignment)
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffForAssignment, setSelectedStaffForAssignment] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningTask, setAssigningTask] = useState(null);
  
  // State for selected staff task (for daily updates)
  const [selectedStaffTask, setSelectedStaffTask] = useState(null);
  
  // State for dialogs
  const [deptTaskDialogOpen, setDeptTaskDialogOpen] = useState(false);
  const [dailyUpdateDialogOpen, setDailyUpdateDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [taskDetailsDialogOpen, setTaskDetailsDialogOpen] = useState(false);
  const [selectedTaskDetails, setSelectedTaskDetails] = useState(null);
  const [confirmCompleteDialogOpen, setConfirmCompleteDialogOpen] = useState(false);
  const [pendingCompleteTask, setPendingCompleteTask] = useState(null);
  const [forceComplete, setForceComplete] = useState(false);
  
  // State for filters - FIXED to only show HOD's department
  const [deptFilters, setDeptFilters] = useState({
    department: 'all', // Will be overridden by currentUser.department
    status: 'all',
    priority: 'all',
    search: '',
  });
  const [staffFilters, setStaffFilters] = useState({
    status: 'all',
    priority: 'all',
    search: '',
  });
  
  // State for pagination
  const [deptPage, setDeptPage] = useState(1);
  const [staffPage, setStaffPage] = useState(1);
  const [rowsPerPage] = useState(5);
  
  // State for forms
  const [deptTaskForm, setDeptTaskForm] = useState({
    department: '',
    task_title: '',
    task_description: '',
    task_type: 'weekly',
    priority: 'medium',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
  });
  
  const [dailyUpdateForm, setDailyUpdateForm] = useState({
    staff_task_id: '',
    update_date: format(new Date(), 'yyyy-MM-dd'),
    hours_spent: 0,
    progress_description: '',
    challenges: '',
    next_steps: '',
    status: 'on_track',
    completion_percentage: 0,
  });
  
  const [commentForm, setCommentForm] = useState({
    task_type: 'staff',
    task_id: '',
    comment: '',
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // ==================== HELPER FUNCTIONS ====================
  
  const getDepartmentProgress = (task) => {
    if (task.staff_task_count === 0) return task.completion_percentage || 0;
    return Math.round((task.completed_staff_count / task.staff_task_count) * 100);
  };
  
  const getDaysRemainingColor = (days) => {
    if (days < 0) return '#f44336';
    if (days < 2) return '#ff9800';
    if (days < 5) return '#ffc107';
    return '#4caf50';
  };
  
  const goToPreviousMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };
  
  const goToToday = () => {
    setCurrentMonth(new Date());
    setSelectedCalendarDate(null);
  };
  
  const handleDayClick = (date, events) => {
    setSelectedCalendarDate(date);
    setSelectedDayEvents(events);
    setCalendarEventDialogOpen(true);
  };
  
  const toggleSidebar = () => setSidebarOpen(prev => !prev);
  
  // HOD specific permissions - HOD can assign to self and perform actions
  const canAssignTasks = useCallback(() => {
    const userType = currentUser.user_type?.toLowerCase();
    const allowedTypes = ['admin', 'gm', 'hod', 'hr', 'manager'];
    return allowedTypes.includes(userType);
  }, [currentUser.user_type]);
  
  const canCreateTasks = useCallback(() => {
    const userType = currentUser.user_type?.toLowerCase();
    const allowedTypes = ['admin', 'gm', 'hod', 'hr', 'manager'];
    return allowedTypes.includes(userType);
  }, [currentUser.user_type]);
  
  const canCompleteDepartmentTasks = useCallback(() => {
    const userType = currentUser.user_type?.toLowerCase();
    const allowedTypes = ['admin', 'gm', 'hod', 'hr', 'manager', 'account', 'auditor'];
    return allowedTypes.includes(userType);
  }, [currentUser.user_type]);
  
  const addDebug = (key, value) => {
    if (debugMode) {
      setDebugInfo(prev => ({ ...prev, [key]: value, timestamp: new Date().toISOString() }));
      console.log(`[DEBUG HOD] ${key}:`, value);
    }
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  // ==================== MEMOIZED VALUES FOR CALENDAR ====================
  
  const groupedCalendarEvents = useMemo(() => {
    const groups = {};
    if (calendarEvents && calendarEvents.length > 0) {
      calendarEvents.forEach(event => {
        if (event.start) {
          const date = format(parseISO(event.start), 'yyyy-MM-dd');
          if (!groups[date]) groups[date] = [];
          groups[date].push(event);
        }
      });
    }
    return groups;
  }, [calendarEvents]);
  
  const generateCalendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDayOfWeek = monthStart.getDay();
    const daysFromPrevMonth = startDayOfWeek;
    const totalDaysToShow = 42;
    
    const calendarDays = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = daysFromPrevMonth; i > 0; i--) {
      const date = new Date(monthStart);
      date.setDate(monthStart.getDate() - i);
      calendarDays.push({
        date,
        isCurrentMonth: false,
        isToday: isSameDay(date, today),
        events: groupedCalendarEvents[format(date, 'yyyy-MM-dd')] || [],
      });
    }
    
    for (let d = 1; d <= monthEnd.getDate(); d++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d);
      calendarDays.push({
        date,
        isCurrentMonth: true,
        isToday: isSameDay(date, today),
        events: groupedCalendarEvents[format(date, 'yyyy-MM-dd')] || [],
      });
    }
    
    const daysNeeded = totalDaysToShow - calendarDays.length;
    for (let i = 1; i <= daysNeeded; i++) {
      const date = new Date(monthEnd);
      date.setDate(monthEnd.getDate() + i);
      calendarDays.push({
        date,
        isCurrentMonth: false,
        isToday: isSameDay(date, today),
        events: groupedCalendarEvents[format(date, 'yyyy-MM-dd')] || [],
      });
    }
    
    return calendarDays;
  }, [currentMonth, groupedCalendarEvents]);
  
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // ==================== DATA FETCHING FUNCTIONS ====================
  
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get_depts_list.php`);
      if (response.data.success) {
        setDepartments(response.data.data);
        addDebug('departments_loaded', response.data.data.length);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
      addDebug('fetch_depts_error', err.message);
    }
  };
  
  // Fetch only staff from HOD's department (including HOD for self-assignment)
  const fetchDepartmentStaff = async () => {
    if (!currentUser.department) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_available_staff&department=${encodeURIComponent(currentUser.department)}`
      );
      if (response.data.success && response.data.data) {
        let formattedStaff = response.data.data.map(s => ({
          phone: s.phone,
          name: s.name,
          department: s.department,
          email: s.email,
        }));
        
        const hodExists = formattedStaff.some(s => s.phone === currentUser.phone);
        if (!hodExists && currentUser.name && currentUser.phone) {
          formattedStaff.unshift({
            phone: currentUser.phone,
            name: currentUser.name,
            department: currentUser.department,
            email: currentUser.email,
          });
          addDebug('hod_added_to_staff_list', { phone: currentUser.phone, name: currentUser.name });
        }
        
        setDepartmentStaff(formattedStaff);
        setStaffList(formattedStaff);
        addDebug('department_staff_loaded', formattedStaff.length);
      } else {
        const fallbackResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get_personalinfo.php`);
        if (fallbackResponse.data.success && fallbackResponse.data.data) {
          let activeStaff = fallbackResponse.data.data.filter(
            s => s.status === 'Active' && s.is_active == 1 && s.department === currentUser.department
          );
          const formattedStaff = activeStaff.map(s => ({
            phone: s.phone,
            name: `${s.surname} ${s.other_names}`,
            department: s.department,
            email: s.email,
          }));
          
          const hodExists = formattedStaff.some(s => s.phone === currentUser.phone);
          if (!hodExists && currentUser.name && currentUser.phone) {
            formattedStaff.unshift({
              phone: currentUser.phone,
              name: currentUser.name,
              department: currentUser.department,
              email: currentUser.email,
            });
          }
          
          setStaffList(formattedStaff);
          setDepartmentStaff(formattedStaff);
          addDebug('staff_list_fallback', formattedStaff.length);
        }
      }
    } catch (err) {
      console.error('Error fetching department staff:', err);
      addDebug('fetch_staff_error', err.message);
      if (currentUser.name && currentUser.phone) {
        const selfStaff = [{
          phone: currentUser.phone,
          name: currentUser.name,
          department: currentUser.department,
          email: currentUser.email,
        }];
        setStaffList(selfStaff);
        setDepartmentStaff(selfStaff);
        addDebug('staff_list_self_only', 'HOD only');
      }
    }
  };
  
  const fetchStaffList = async () => {
    if (!currentUser.department) {
      console.log('[DEBUG] No department found for HOD user');
      return;
    }
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_available_staff&department=${encodeURIComponent(currentUser.department)}`
      );
      if (response.data.success && response.data.data) {
        let formattedStaff = response.data.data.map(s => ({
          phone: s.phone,
          name: s.name,
          department: s.department,
          email: s.email,
        }));
        
        const hodExists = formattedStaff.some(s => s.phone === currentUser.phone);
        if (!hodExists && currentUser.name && currentUser.phone) {
          formattedStaff.unshift({
            phone: currentUser.phone,
            name: currentUser.name,
            department: currentUser.department,
            email: currentUser.email,
          });
        }
        
        setStaffList(formattedStaff);
        addDebug('staff_list_loaded', formattedStaff.length);
      } else {
        const fallbackResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get_personalinfo.php`);
        if (fallbackResponse.data.success && fallbackResponse.data.data) {
          let activeStaff = fallbackResponse.data.data.filter(
            s => s.status === 'Active' && s.is_active == 1 && s.department === currentUser.department
          );
          let formattedStaff = activeStaff.map(s => ({
            phone: s.phone,
            name: `${s.surname} ${s.other_names}`,
            department: s.department,
            email: s.email,
          }));
          
          const hodExists = formattedStaff.some(s => s.phone === currentUser.phone);
          if (!hodExists && currentUser.name && currentUser.phone) {
            formattedStaff.unshift({
              phone: currentUser.phone,
              name: currentUser.name,
              department: currentUser.department,
              email: currentUser.email,
            });
          }
          
          setStaffList(formattedStaff);
          addDebug('staff_list_fallback', formattedStaff.length);
        }
      }
    } catch (err) {
      console.error('Error fetching staff list:', err);
      addDebug('fetch_staff_error', err.message);
      if (currentUser.name && currentUser.phone) {
        setStaffList([{
          phone: currentUser.phone,
          name: currentUser.name,
          department: currentUser.department,
          email: currentUser.email,
        }]);
      }
    }
  };
  
  const fetchDepartmentTasks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (currentUser.department) {
        params.append('department', currentUser.department);
      }
      
      if (deptFilters.status && deptFilters.status !== 'all') {
        params.append('status', deptFilters.status);
      }
      if (deptFilters.priority && deptFilters.priority !== 'all') {
        params.append('priority', deptFilters.priority);
      }
      if (deptFilters.search && deptFilters.search.trim() !== '') {
        params.append('search', deptFilters.search.trim());
      }
      params.append('page', deptPage);
      params.append('limit', rowsPerPage);
      
      addDebug('dept_fetch_params', Object.fromEntries(params));
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_department_tasks&${params}`
      );
      
      addDebug('dept_tasks_response', { 
        success: response.data.success, 
        count: response.data.data?.tasks?.length,
        pagination: response.data.data?.pagination
      });
      
      if (response.data.success) {
        setDepartmentTasks(response.data.data.tasks || []);
        if (response.data.data.pagination) {
          setDepartmentPagination(response.data.data.pagination);
        }
      } else {
        console.error('API returned error:', response.data.message);
        showSnackbar(response.data.message || 'Error fetching department tasks', 'error');
      }
    } catch (err) {
      console.error('Error fetching department tasks:', err);
      addDebug('fetch_dept_error', err.message);
      showSnackbar('Error fetching department tasks. Please check console.', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStaffTasks = async () => {
    if (!currentUser.phone) return;
    
    try {
      const params = new URLSearchParams();
      if (staffFilters.status && staffFilters.status !== 'all') {
        params.append('status', staffFilters.status);
      }
      if (staffFilters.priority && staffFilters.priority !== 'all') {
        params.append('priority', staffFilters.priority);
      }
      if (staffFilters.search && staffFilters.search.trim() !== '') {
        params.append('search', staffFilters.search.trim());
      }
      
      params.append('page', staffPage);
      params.append('limit', rowsPerPage);
      
      console.log('[DEBUG HOD] Fetching staff tasks with params:', params.toString());
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_staff_tasks&${params}`
      );
      
      if (response.data.success) {
        let tasks = response.data.data.tasks || [];
        
        if (currentUser.department) {
          tasks = tasks.filter(task => task.department_name === currentUser.department);
        }
        
        setStaffTasks(tasks);
        if (response.data.data.pagination) {
          setStaffPagination({
            ...response.data.data.pagination,
            total: tasks.length,
            total_pages: Math.ceil(tasks.length / rowsPerPage)
          });
        }
        console.log('[DEBUG HOD] Loaded staff tasks:', tasks.length);
      } else {
        console.error('[DEBUG HOD] Staff tasks API error:', response.data.message);
        showSnackbar(response.data.message || 'Error loading tasks', 'error');
      }
    } catch (err) {
      console.error('Error fetching staff tasks:', err);
      if (err.response) {
        console.error('[DEBUG HOD] Response status:', err.response.status);
        console.error('[DEBUG HOD] Response data:', err.response.data);
      }
      showSnackbar('Error loading tasks. Please try refreshing.', 'error');
    }
  };
  
  // Fetch daily updates for a specific task
  const fetchDailyUpdatesForTask = async (taskId, taskTitle) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_daily_updates&staff_task_id=${taskId}`
      );
      
      if (response.data.success) {
        setSelectedTaskUpdates(response.data.data || []);
        setSelectedTaskForUpdates({ id: taskId, title: taskTitle });
        setDailyUpdatesDialogOpen(true);
        addDebug('daily_updates_fetched', { taskId, count: response.data.data?.length });
      } else {
        showSnackbar('Could not load daily updates', 'error');
      }
    } catch (err) {
      console.error('Error fetching daily updates:', err);
      showSnackbar('Error loading daily updates', 'error');
    }
  };
  
  const fetchNotifications = async () => {
    if (!currentUser.phone) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_notifications&phone=${currentUser.phone}`
      );
      if (response.data.success) {
        setNotifications(response.data.data);
        const unread = response.data.data.filter(n => !n.is_read).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };
  
  const fetchStats = useCallback(async () => {
    if (!currentUser.phone) return;
    
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_task_stats&department=${encodeURIComponent(currentUser.department)}`;
      console.log('[DEBUG HOD] Fetching stats from:', url);
      
      const response = await axios.get(url);
      console.log('[DEBUG HOD] Stats response:', response.data);
      
      if (response.data.success && response.data.data) {
        const newStats = {
          department_tasks: {
            total: Number(response.data.data.department_tasks?.total) || 0,
            pending: Number(response.data.data.department_tasks?.pending) || 0,
            in_progress: Number(response.data.data.department_tasks?.in_progress) || 0,
            completed: Number(response.data.data.department_tasks?.completed) || 0,
            cancelled: Number(response.data.data.department_tasks?.cancelled) || 0,
            overdue: Number(response.data.data.department_tasks?.overdue) || 0,
            avg_completion: Number(response.data.data.department_tasks?.avg_completion) || 0
          },
          staff_tasks: {
            total: Number(response.data.data.staff_tasks?.total) || 0,
            pending: Number(response.data.data.staff_tasks?.pending) || 0,
            in_progress: Number(response.data.data.staff_tasks?.in_progress) || 0,
            completed: Number(response.data.data.staff_tasks?.completed) || 0,
            cancelled: Number(response.data.data.staff_tasks?.cancelled) || 0,
            overdue: Number(response.data.data.staff_tasks?.overdue) || 0,
            avg_completion: Number(response.data.data.staff_tasks?.avg_completion) || 0,
            total_estimated_hours: Number(response.data.data.staff_tasks?.total_estimated_hours) || 0,
            completed_hours: Number(response.data.data.staff_tasks?.completed_hours) || 0
          },
          due_soon: Number(response.data.data.due_soon) || 0,
          completed_this_week: Number(response.data.data.completed_this_week) || 0
        };
        
        setStats(newStats);
        console.log('[DEBUG HOD] Stats updated:', newStats);
      } else {
        console.warn('[DEBUG HOD] Invalid stats response:', response.data);
      }
    } catch (err) {
      console.error('[DEBUG HOD] Error fetching stats:', err);
      if (err.response) {
        console.error('[DEBUG HOD] Error response:', err.response.data);
      }
    }
  }, [currentUser.phone, currentUser.department]);
  
  const fetchDepartmentStats = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_department_stats`);
      if (response.data.success) {
        const filteredStats = response.data.data.filter(
          stat => stat.department === currentUser.department
        );
        setDepartmentStats(filteredStats);
        console.log('[DEBUG HOD] Department stats loaded:', filteredStats.length);
      }
    } catch (err) {
      console.error('Error fetching department stats:', err);
    }
  };
  
  const fetchTimeline = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_task_timeline&department=${encodeURIComponent(currentUser.department)}`
      );
      if (response.data.success) {
        setTimeline(response.data.data);
        console.log('[DEBUG HOD] Timeline loaded:', response.data.data?.length);
      }
    } catch (err) {
      console.error('Error fetching timeline:', err);
    }
  };
  
  const fetchCalendarEvents = async () => {
    try {
      const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      
      let url = `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_calendar_events&start=${startDate}&end=${endDate}&user_type=${encodeURIComponent(currentUser.user_type || '')}&department=${encodeURIComponent(currentUser.department || '')}`;
      if (currentUser.phone) url += `&phone=${encodeURIComponent(currentUser.phone)}`;
      
      const response = await axios.get(url);
      
      if (response.data?.success) {
        let events = response.data.data || [];
        if (currentUser.department) {
          events = events.filter(event => 
            event.extendedProps?.department === currentUser.department
          );
        }
        setCalendarEvents(events);
        console.log('[DEBUG HOD] Calendar events loaded:', events.length);
      } else {
        setCalendarEvents([]);
      }
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      setCalendarEvents([]);
    }
  };
  
  const refreshAllData = async () => {
    setRefreshKey(prev => prev + 1);
    setLoading(true);
    
    try {
      await Promise.all([
        fetchDepartmentTasks(),
        fetchStaffTasks(),
        fetchNotifications(),
        fetchStats(),
        fetchDepartmentStats(),
        fetchTimeline(),
        fetchCalendarEvents(),
      ]);
      
      showSnackbar('Data refreshed successfully', 'success');
    } catch (err) {
      console.error('Error during refresh:', err);
      showSnackbar('Error refreshing data', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // ==================== ACTION FUNCTIONS ====================
  
  const updateTaskStatus = async (taskId, newStatus, taskType = 'staff', completionPercentage = null) => {
    try {
      const endpoint = taskType === 'staff' 
        ? `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=update_staff_task_status`
        : `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=update_department_task_status`;
      
      const payload = { task_id: taskId, status: newStatus };
      if (completionPercentage !== null) {
        payload.completion_percentage = completionPercentage;
      }
      
      const response = await axios.post(endpoint, payload);
      
      if (response.data.success) {
        showSnackbar(`Task status updated to ${newStatus}`, 'success');
        await refreshAllData();
        return true;
      } else {
        showSnackbar(response.data.message || 'Failed to update status', 'error');
        return false;
      }
    } catch (err) {
      console.error('Error updating task status:', err);
      showSnackbar(err.response?.data?.message || 'Error updating status', 'error');
      return false;
    }
  };
  
  const markDepartmentTaskComplete = async (taskId, force = false) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=mark_department_task_complete`,
        {
          task_id: taskId,
          force_complete: force,
          completed_by: currentUser.name,
        }
      );
      
      if (response.data.success) {
        if (response.data.data?.requires_force) {
          setPendingCompleteTask({
            id: taskId,
            task_title: departmentTasks.find(t => t.id === taskId)?.task_title,
            completed_staff: response.data.data.completed_staff,
            total_staff: response.data.data.total_staff
          });
          setConfirmCompleteDialogOpen(true);
          return false;
        }
        
        showSnackbar(response.data.message || 'Department task completed successfully', 'success');
        await refreshAllData();
        return true;
      } else {
        showSnackbar(response.data.message || 'Failed to complete task', 'error');
        return false;
      }
    } catch (err) {
      console.error('Error marking department task complete:', err);
      showSnackbar(err.response?.data?.message || 'Error completing task', 'error');
      return false;
    }
  };
  
  const handleForceCompleteConfirm = async () => {
    if (pendingCompleteTask) {
      await markDepartmentTaskComplete(pendingCompleteTask.id, true);
      setConfirmCompleteDialogOpen(false);
      setPendingCompleteTask(null);
      setForceComplete(false);
    }
  };
  
  const handleMarkComplete = async (taskId) => {
    if (!window.confirm('Are you sure you want to mark this task as completed?')) {
      return;
    }
    await updateTaskStatus(taskId, 'completed', 'staff', 100);
  };
  
  const handleCreateDeptTask = async () => {
    if (!deptTaskForm.task_title) {
      showSnackbar('Please fill all required fields', 'warning');
      return;
    }
    
    if (deptTaskForm.start_date && deptTaskForm.end_date && deptTaskForm.start_date > deptTaskForm.end_date) {
      showSnackbar('Start date cannot be after end date', 'warning');
      return;
    }
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=create_department_task`,
        {
          ...deptTaskForm,
          department: currentUser.department,
          assigned_by: currentUser.name,
          assigned_by_phone: currentUser.phone,
        }
      );
      
      if (response.data.success) {
        showSnackbar('Department task created successfully', 'success');
        setDeptTaskDialogOpen(false);
        setDeptTaskForm({
          department: currentUser.department,
          task_title: '',
          task_description: '',
          task_type: 'weekly',
          priority: 'medium',
          start_date: format(new Date(), 'yyyy-MM-dd'),
          end_date: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
        });
        await refreshAllData();
        setDeptPage(1);
      } else {
        showSnackbar(response.data.message || 'Failed to create task', 'error');
      }
    } catch (err) {
      console.error('Error creating department task:', err);
      showSnackbar(err.response?.data?.message || 'Error creating task', 'error');
    }
  };
  
  const openAssignDialog = (task) => {
    setAssigningTask(task);
    setSelectedStaffForAssignment(null);
    setAssignDialogOpen(true);
  };
  
  const handleAssignTaskToStaff = async () => {
    if (!selectedStaffForAssignment || !assigningTask) {
      showSnackbar('Please select a staff member', 'warning');
      return;
    }
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=assign_task_to_staff`,
        {
          department_task_id: assigningTask.id,
          phone: selectedStaffForAssignment.phone,
          staff_name: selectedStaffForAssignment.name,
          assigned_by: currentUser.name,
          assigned_by_phone: currentUser.phone,
          deadline: format(addDays(new Date(), 7), 'yyyy-MM-dd HH:mm:ss'),
        }
      );
      
      if (response.data.success) {
        showSnackbar(`Task "${assigningTask.task_title}" assigned to ${selectedStaffForAssignment.name} successfully`, 'success');
        setAssignDialogOpen(false);
        setAssigningTask(null);
        setSelectedStaffForAssignment(null);
        await refreshAllData();
      } else {
        showSnackbar(response.data.message || 'Failed to assign task', 'error');
      }
    } catch (err) {
      console.error('Error assigning task:', err);
      showSnackbar(err.response?.data?.message || 'Error assigning task', 'error');
    }
  };
  
  const handleAddDailyUpdate = async () => {
    if (!dailyUpdateForm.staff_task_id) {
      showSnackbar('Task ID is missing. Please select a task first.', 'warning');
      return;
    }
    
    if (!dailyUpdateForm.progress_description || dailyUpdateForm.progress_description.trim() === '') {
      showSnackbar('Progress description is required', 'warning');
      return;
    }
    
    console.log('[DEBUG HOD] Submitting daily update:', dailyUpdateForm);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=add_daily_update`,
        {
          staff_task_id: dailyUpdateForm.staff_task_id,
          phone: currentUser.phone,
          update_date: dailyUpdateForm.update_date,
          hours_spent: parseFloat(dailyUpdateForm.hours_spent) || 0,
          progress_description: dailyUpdateForm.progress_description.trim(),
          challenges: dailyUpdateForm.challenges || '',
          next_steps: dailyUpdateForm.next_steps || '',
          status: dailyUpdateForm.status,
          completion_percentage: parseInt(dailyUpdateForm.completion_percentage) || 0,
        }
      );
      
      console.log('[DEBUG HOD] Daily update response:', response.data);
      
      if (response.data.success) {
        showSnackbar('Daily update added successfully', 'success');
        setDailyUpdateDialogOpen(false);
        setDailyUpdateForm({
          staff_task_id: '',
          update_date: format(new Date(), 'yyyy-MM-dd'),
          hours_spent: 0,
          progress_description: '',
          challenges: '',
          next_steps: '',
          status: 'on_track',
          completion_percentage: 0,
        });
        await refreshAllData();
      } else {
        const errorMessage = response.data.message || 'Failed to add update';
        const errors = response.data.errors || [];
        showSnackbar(errors.length > 0 ? errors[0] : errorMessage, 'error');
        console.error('[DEBUG HOD] Daily update error:', response.data);
      }
    } catch (err) {
      console.error('Error adding daily update:', err);
      if (err.response?.data) {
        console.error('[DEBUG HOD] Error response:', err.response.data);
        const errorMsg = err.response.data.message || err.response.data.errors?.join(', ') || 'Error adding update';
        showSnackbar(errorMsg, 'error');
      } else {
        showSnackbar('Network error. Please check your connection.', 'error');
      }
    }
  };
  
  const handleAddComment = async () => {
    if (!commentForm.comment.trim()) {
      showSnackbar('Please enter a comment', 'warning');
      return;
    }
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=add_comment`,
        {
          ...commentForm,
          phone: currentUser.phone,
          commenter_name: currentUser.name,
        }
      );
      
      if (response.data.success) {
        showSnackbar('Comment added successfully', 'success');
        setCommentDialogOpen(false);
        setCommentForm({
          task_type: 'staff',
          task_id: '',
          comment: '',
        });
        await refreshAllData();
      } else {
        showSnackbar(response.data.message || 'Failed to add comment', 'error');
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      showSnackbar(err.response?.data?.message || 'Error adding comment', 'error');
    }
  };
  
  const markNotificationRead = async (notificationId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=mark_notification_read`,
        { notification_id: notificationId }
      );
      fetchNotifications();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };
  
  const markAllNotificationsRead = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=mark_all_notifications_read`,
        { phone: currentUser.phone }
      );
      fetchNotifications();
      showSnackbar('All notifications marked as read', 'success');
    } catch (err) {
      console.error('Error marking notifications as read:', err);
    }
  };
  
  const deleteDeptTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this department task? This will also remove all assigned staff tasks.')) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=delete_department_task`,
        { task_id: taskId }
      );
      
      if (response.data.success) {
        showSnackbar('Department task deleted successfully', 'success');
        await refreshAllData();
      } else {
        showSnackbar(response.data.message || 'Failed to delete task', 'error');
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      showSnackbar(err.response?.data?.message || 'Error deleting task', 'error');
    }
  };
  
  const viewTaskDetails = async (task, type = 'staff') => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=${type === 'staff' ? 'get_staff_task' : 'get_department_task'}&id=${task.id}`
      );
      
      if (response.data.success) {
        setSelectedTaskDetails(response.data.data);
        setTaskDetailsDialogOpen(true);
      }
    } catch (err) {
      console.error('Error loading task details:', err);
      showSnackbar('Error loading task details', 'error');
    }
  };
  
  // ==================== LOAD USER DATA ====================
  
  const loadUserData = async () => {
    setInitialLoading(true);
    try {
      const userPhone = localStorage.getItem('phoneNumber') || 
                        localStorage.getItem('userPhone') || 
                        localStorage.getItem('phone') || 
                        '';
      const userName = localStorage.getItem('userName') || 
                       localStorage.getItem('full_name') || 
                       localStorage.getItem('name') || 
                       'HOD User';
      const userDept = localStorage.getItem('userDepartment') || 
                       localStorage.getItem('department') || 
                       '';
      const userEmail = localStorage.getItem('email') || 
                        localStorage.getItem('userEmail') || 
                        '';
      const userType = localStorage.getItem('role') || 
                       localStorage.getItem('user_type') || 
                       'hod';
      
      console.log('[DEBUG HOD] Loading user data:', { userPhone, userName, userDept, userEmail, userType });
      
      setCurrentUser({
        phone: userPhone || '',
        name: userName,
        department: userDept,
        email: userEmail,
        user_type: userType,
      });
      
      if (userDept) {
        setDeptFilters(prev => ({ ...prev, department: userDept }));
        setDeptTaskForm(prev => ({ ...prev, department: userDept }));
      }
      
      await fetchDepartments();
      await fetchStaffList();
      await fetchDepartmentStaff();
      await refreshAllData();
    } catch (error) {
      console.error('Error loading user data:', error);
      showSnackbar('Error loading user data', 'error');
    } finally {
      setInitialLoading(false);
    }
  };
  
  // ==================== EFFECTS ====================
  
  useEffect(() => {
    loadUserData();
  }, []);
  
  useEffect(() => {
    if (currentUser.department) {
      fetchStaffList();
      fetchDepartmentStaff();
    }
  }, [currentUser.department]);
  
  useEffect(() => {
    if (!initialLoading && currentUser.department) {
      fetchDepartmentTasks();
    }
  }, [deptFilters, deptPage, initialLoading, currentUser.department]);
  
  useEffect(() => {
    if (!initialLoading && currentUser.phone) {
      const debounceTimer = setTimeout(() => {
        fetchStaffTasks();
      }, 500);
      return () => clearTimeout(debounceTimer);
    }
  }, [staffFilters, staffPage, currentUser.phone, initialLoading]);
  
  useEffect(() => {
    if (!initialLoading && currentUser.phone) {
      fetchCalendarEvents();
    }
  }, [currentMonth, currentUser.phone, initialLoading, currentUser.department]);
  
  useEffect(() => {
    if (!initialLoading && currentUser.phone) {
      fetchStats();
    }
  }, [activeTab, staffTasks, departmentTasks, currentUser.phone, initialLoading, fetchStats, currentUser.department]);
  
  useEffect(() => {
    if (!initialLoading && currentUser.phone) {
      fetchStats();
    }
  }, [refreshKey, currentUser.phone, initialLoading, fetchStats]);
  
  // ==================== RENDER FUNCTIONS ====================
  
  const renderStatCards = () => {
    const statItems = [
      {
        title: 'Department Tasks',
        value: stats.department_tasks?.total || 0,
        icon: <Assignment sx={{ fontSize: 40 }} />,
        subtext: `${stats.department_tasks?.completed || 0} completed`,
        bgGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      {
        title: 'Staff Tasks',
        value: stats.staff_tasks?.total || 0,
        icon: <Group sx={{ fontSize: 40 }} />,
        subtext: `${stats.staff_tasks?.in_progress || 0} in progress`,
        bgGradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      },
      {
        title: 'Completed',
        value: stats.staff_tasks?.completed || 0,
        icon: <CheckCircle sx={{ fontSize: 40 }} />,
        subtext: `${stats.completed_this_week || 0} this week`,
        bgGradient: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
      },
      {
        title: 'Due Soon',
        value: stats.due_soon || 0,
        icon: <WarningIcon sx={{ fontSize: 40 }} />,
        subtext: 'Due in 7 days',
        bgGradient: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
      },
      {
        title: 'Overdue',
        value: stats.staff_tasks?.overdue || 0,
        icon: <Cancel sx={{ fontSize: 40 }} />,
        subtext: 'Past deadline',
        bgGradient: 'linear-gradient(135deg, #f44336 0%, #ff7961 100%)',
      },
      {
        title: 'Department',
        value: currentUser.department || 'N/A',
        icon: <BusinessCenter sx={{ fontSize: 40 }} />,
        subtext: 'Your department',
        bgGradient: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
      },
    ];
    
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statItems.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <StyledCard sx={{ background: stat.bgGradient, color: '#fff' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h3" fontWeight="bold">{stat.value}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>{stat.title}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                      {stat.subtext}
                    </Typography>
                  </Box>
                  <Box sx={{ opacity: 0.8 }}>{stat.icon}</Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
    );
  };
  
  const renderDepartmentTasksTable = () => {
    const showAssignButton = canAssignTasks();
    const showCreateButton = canCreateTasks();
    const showCompleteButton = canCompleteDepartmentTasks();
    
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h5" fontWeight="bold">
            <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
            Department Tasks - {currentUser.department}
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search tasks..."
              value={deptFilters.search}
              onChange={(e) => {
                setDeptFilters({ ...deptFilters, search: e.target.value });
                setDeptPage(1);
              }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
              sx={{ minWidth: 200 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={deptFilters.status}
                onChange={(e) => {
                  setDeptFilters({ ...deptFilters, status: e.target.value });
                  setDeptPage(1);
                }}
                displayEmpty
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={deptFilters.priority}
                onChange={(e) => {
                  setDeptFilters({ ...deptFilters, priority: e.target.value });
                  setDeptPage(1);
                }}
                displayEmpty
              >
                <MenuItem value="all">All Priorities</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
            
            {showCreateButton && (
              <Button variant="contained" startIcon={<Add />} onClick={() => setDeptTaskDialogOpen(true)}>
                Create Task
              </Button>
            )}
            <Button variant="outlined" startIcon={<Refresh />} onClick={refreshAllData}>
              Refresh
            </Button>
          </Box>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>Task Title</strong></TableCell>
                <TableCell><strong>Assigned By</strong></TableCell>
                <TableCell><strong>Priority</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Dates</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Progress</strong></TableCell>
                <TableCell><strong>Assigned Staff</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 8 }}><CircularProgress /></TableCell></TableRow>
              ) : departmentTasks.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                  <Typography color="textSecondary">No department tasks found. Click "Create Task" to add one.</Typography>
                </TableCell></TableRow>
              ) : (
                departmentTasks.map((task) => {
                  const progress = getDepartmentProgress(task);
                  const daysRemaining = task.days_remaining;
                  const isCompleted = task.status === 'completed';
                  
                  return (
                    <TableRow key={task.id} hover sx={{ bgcolor: isCompleted ? alpha('#4caf50', 0.05) : 'inherit' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{task.task_title}</Typography>
                        {task.task_description && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            {task.task_description.substring(0, 60)}...
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Assigned by: ${task.assigned_by || 'Unknown'}`}>
                          <Chip size="small" icon={<Person fontSize="small" />} label={task.assigned_by || 'N/A'} variant="outlined" />
                        </Tooltip>
                      </TableCell>
                      <TableCell><PriorityBadge label={task.priority.toUpperCase()} priority={task.priority} size="small" /></TableCell>
                      <TableCell><Chip label={task.task_type} size="small" variant="outlined" /></TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(parseISO(task.start_date), 'MMM dd')} - {format(parseISO(task.end_date), 'MMM dd')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: getDaysRemainingColor(daysRemaining) }}>
                          {daysRemaining < 0 ? 'Overdue' : `${daysRemaining} days left`}
                        </Typography>
                      </TableCell>
                      <TableCell><StatusChip label={task.status.replace('_', ' ').toUpperCase()} status={task.status} size="small" /></TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 100 }}>
                          <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3, mb: 0.5 }} />
                          <Typography variant="caption" color="textSecondary">{progress}% ({task.completed_staff_count}/{task.staff_task_count} staff)</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {task.assigned_staff && task.assigned_staff.length > 0 ? (
                          <Tooltip title={task.assigned_staff.map(s => s.staff_name).join(', ')}>
                            <Chip size="small" icon={<Person fontSize="small" />} label={`${task.assigned_staff.length} staff`} variant="outlined" />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="textSecondary">Not assigned</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => viewTaskDetails(task, 'department')} title="View Details">
                          <Visibility fontSize="small" />
                        </IconButton>
                        {showAssignButton && task.status !== 'completed' && (
                          <IconButton size="small" color="primary" onClick={() => openAssignDialog(task)} title="Assign to Staff (including self)">
                            <PersonAdd fontSize="small" />
                          </IconButton>
                        )}
                        {showCompleteButton && task.status !== 'completed' && (
                          <IconButton size="small" color="success" onClick={() => markDepartmentTaskComplete(task.id, false)} title="Mark Complete">
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        )}
                        {(currentUser.user_type === 'admin' || currentUser.user_type === 'gm' || currentUser.user_type === 'hod') && (
                          <IconButton size="small" color="error" onClick={() => deleteDeptTask(task.id)} title="Delete">
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {departmentPagination.total_pages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination 
              count={departmentPagination.total_pages} 
              page={deptPage} 
              onChange={(e, value) => setDeptPage(value)} 
              color="primary" 
            />
          </Box>
        )}
      </Paper>
    );
  };
  
  const renderStaffTasksTable = () => {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h5" fontWeight="bold">
            <Group sx={{ mr: 1, verticalAlign: 'middle' }} />
            Staff Tasks - {currentUser.department}
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search tasks..."
              value={staffFilters.search}
              onChange={(e) => {
                setStaffFilters({ ...staffFilters, search: e.target.value });
                setStaffPage(1);
              }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select 
                value={staffFilters.status} 
                onChange={(e) => {
                  setStaffFilters({ ...staffFilters, status: e.target.value });
                  setStaffPage(1);
                }} 
                displayEmpty
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select 
                value={staffFilters.priority} 
                onChange={(e) => {
                  setStaffFilters({ ...staffFilters, priority: e.target.value });
                  setStaffPage(1);
                }} 
                displayEmpty
              >
                <MenuItem value="all">All Priorities</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<Refresh />} onClick={refreshAllData}>Refresh</Button>
          </Box>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>Staff Name</strong></TableCell>
                <TableCell><strong>Task Title</strong></TableCell>
                <TableCell><strong>Priority</strong></TableCell>
                <TableCell><strong>Deadline</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Progress</strong></TableCell>
                <TableCell><strong>Last Update</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 8 }}><CircularProgress /></TableCell></TableRow>
              ) : staffTasks.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                  <Typography color="textSecondary">No staff tasks found in your department</Typography>
                </TableCell></TableRow>
              ) : (
                staffTasks.map((task) => {
                  const isOverdue = new Date(task.deadline) < new Date() && task.status !== 'completed';
                  const daysRemaining = differenceInDays(parseISO(task.deadline), new Date());
                  const isCurrentUserTask = task.phone === currentUser.phone;
                  const hasUpdates = task.daily_update_count > 0;
                  
                  return (
                    <TableRow key={task.id} hover sx={{ bgcolor: isOverdue ? '#ffebee' : hasUpdates ? alpha('#2196f3', 0.03) : 'inherit' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {task.staff_name}
                          {isCurrentUserTask && (
                            <Chip 
                              size="small" 
                              label="You" 
                              color="primary" 
                              variant="outlined" 
                              sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} 
                            />
                          )}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">{task.phone}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{task.task_title}</Typography>
                        {task.task_description && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            {task.task_description.substring(0, 50)}...
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell><PriorityBadge label={task.priority.toUpperCase()} priority={task.priority} size="small" /></TableCell>
                      <TableCell>
                        <Typography variant="body2" color={isOverdue ? 'error' : 'textPrimary'}>
                          {format(parseISO(task.deadline), 'MMM dd, yyyy HH:mm')}
                        </Typography>
                        {!isOverdue && daysRemaining > 0 && (
                          <Typography variant="caption" sx={{ color: getDaysRemainingColor(daysRemaining) }}>
                            {daysRemaining} days left
                          </Typography>
                        )}
                        {isOverdue && <Typography variant="caption" color="error">Overdue</Typography>}
                      </TableCell>
                      <TableCell><StatusChip label={task.status.replace('_', ' ').toUpperCase()} status={task.status} size="small" /></TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 100 }}>
                          <LinearProgress variant="determinate" value={task.completion_percentage || 0} sx={{ height: 6, borderRadius: 3, mb: 0.5 }} />
                          <Typography variant="caption" color="textSecondary">{task.completion_percentage || 0}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {task.last_update_at ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="caption">
                              {format(parseISO(task.last_update_at), 'MMM dd')}
                            </Typography>
                            {hasUpdates && (
                              <Chip 
                                size="small" 
                                label={`${task.daily_update_count} updates`} 
                                variant="outlined" 
                                sx={{ height: 18, fontSize: '0.6rem' }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="textSecondary">No updates</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => viewTaskDetails(task, 'staff')} title="View Details">
                          <Visibility fontSize="small" />
                        </IconButton>
                        {hasUpdates && (
                          <IconButton 
                            size="small" 
                            color="info" 
                            onClick={() => fetchDailyUpdatesForTask(task.id, task.task_title)}
                            title="View Daily Updates & Progress Reports"
                          >
                            <History fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => {
                          setCommentForm({ ...commentForm, task_id: task.id, task_type: 'staff' });
                          setCommentDialogOpen(true);
                        }} title="Add Comment">
                          <Comment fontSize="small" />
                        </IconButton>
                        {isCurrentUserTask && task.status !== 'completed' && task.status !== 'cancelled' && (
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => {
                              setSelectedStaffTask(task);
                              setDailyUpdateForm({ 
                                ...dailyUpdateForm, 
                                staff_task_id: task.id, 
                                completion_percentage: task.completion_percentage || 0 
                              });
                              setDailyUpdateDialogOpen(true);
                            }} 
                            title="Add Daily Update"
                          >
                            <Update fontSize="small" />
                          </IconButton>
                        )}
                        {isCurrentUserTask && task.status !== 'completed' && task.status !== 'cancelled' && (
                          <IconButton size="small" color="success" onClick={() => handleMarkComplete(task.id)} title="Mark Complete">
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {staffPagination.total_pages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination 
              count={staffPagination.total_pages} 
              page={staffPage} 
              onChange={(e, value) => setStaffPage(value)} 
              color="primary" 
            />
          </Box>
        )}
      </Paper>
    );
  };
  
  // Daily Updates Dialog - View staff daily updates and progress reports
  const renderDailyUpdatesDialog = () => {
    return (
      <Dialog open={dailyUpdatesDialogOpen} onClose={() => setDailyUpdatesDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              <History sx={{ mr: 1, verticalAlign: 'middle' }} />
              Daily Updates & Progress Reports - {selectedTaskForUpdates?.title || 'Task'}
            </Typography>
            <IconButton onClick={() => setDailyUpdatesDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTaskUpdates.length === 0 ? (
            <Typography color="textSecondary" align="center" py={4}>
              No daily updates have been submitted for this task yet.
            </Typography>
          ) : (
            <List>
              {selectedTaskUpdates.map((update, index) => (
                <Accordion key={index} elevation={1} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" width="100%">
                      <Typography variant="subtitle2" fontWeight="bold">
                        {format(parseISO(update.update_date), 'EEEE, MMMM dd, yyyy')}
                      </Typography>
                      <Chip size="small" label={`${update.hours_spent || 0} hours`} variant="outlined" />
                      <Chip 
                        size="small" 
                        label={update.status} 
                        variant="outlined" 
                        color={update.status === 'completed' ? 'success' : update.status === 'blocked' ? 'error' : 'info'} 
                      />
                      <Chip size="small" label={`Progress: ${update.completion_percentage || 0}%`} variant="outlined" color="primary" />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        <strong>Progress Description:</strong>
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f9f9f9' }}>
                        <Typography variant="body2">{update.progress_description}</Typography>
                      </Paper>
                      
                      {update.challenges && (
                        <>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            <strong>Challenges Faced:</strong>
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff3e0' }}>
                            <Typography variant="body2" color="error.dark">{update.challenges}</Typography>
                          </Paper>
                        </>
                      )}
                      
                      {update.next_steps && (
                        <>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            <strong>Next Steps / Plan:</strong>
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 2, mb: 1, bgcolor: '#e8f5e9' }}>
                            <Typography variant="body2" color="success.dark">{update.next_steps}</Typography>
                          </Paper>
                        </>
                      )}
                      
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                        Submitted: {format(parseISO(update.submitted_at), 'MMM dd, yyyy HH:mm:ss')}
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDailyUpdatesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };
  
  const renderAnalyticsCharts = () => {
    const COLORS = ['#4caf50', '#ff9800', '#2196f3', '#f44336', '#9e9e9e'];
    
    const pieData = [
      { name: 'Completed', value: stats.staff_tasks?.completed || 0 },
      { name: 'In Progress', value: stats.staff_tasks?.in_progress || 0 },
      { name: 'Pending', value: stats.staff_tasks?.pending || 0 },
      { name: 'Overdue', value: stats.staff_tasks?.overdue || 0 },
      { name: 'Cancelled', value: stats.staff_tasks?.cancelled || 0 },
    ].filter(d => d.value > 0);
    
    return (
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 4 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                Department Performance - {currentUser.department}
              </Typography>
              {departmentStats.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={departmentStats} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="department" angle={-45} textAnchor="end" height={80} />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="total_tasks" fill="#8884d8" name="Total Tasks" />
                    <Bar dataKey="completed_tasks" fill="#82ca9d" name="Completed" />
                    <Bar dataKey="overdue_tasks" fill="#ff7300" name="Overdue" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="textSecondary" align="center" py={4}>No department data available</Typography>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, borderRadius: 4 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Task Timeline (Last 6 Months)
              </Typography>
              {timeline.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={timeline} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month_label" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Area type="monotone" dataKey="tasks_created" stackId="1" stroke="#8884d8" fill="#8884d8" name="Tasks Created" />
                    <Area type="monotone" dataKey="tasks_completed" stackId="2" stroke="#82ca9d" fill="#82ca9d" name="Tasks Completed" />
                    <Area type="monotone" dataKey="staff_tasks_completed" stackId="3" stroke="#ffc658" fill="#ffc658" name="Staff Tasks Completed" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="textSecondary" align="center" py={4}>No timeline data available</Typography>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 4 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Task Distribution</Typography>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="textSecondary" align="center" py={4}>No task data available</Typography>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>Key Metrics</Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">Completion Rate</Typography>
                  <Typography variant="h6" color="success.main">{Math.round(stats.staff_tasks?.avg_completion || 0)}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={stats.staff_tasks?.avg_completion || 0} sx={{ height: 8, borderRadius: 4, mb: 2 }} />
                
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">Productivity</Typography>
                  <Typography variant="h6">{stats.staff_tasks?.completed_hours || 0}/{stats.staff_tasks?.total_estimated_hours || 0} hrs</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.staff_tasks?.total_estimated_hours > 0 ? (stats.staff_tasks?.completed_hours || 0) / (stats.staff_tasks?.total_estimated_hours || 1) * 100 : 0} 
                  sx={{ height: 8, borderRadius: 4 }} 
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };
  
  const renderCalendarView = () => {
    const hasEvents = calendarEvents.length > 0;
    
    return (
      <Paper sx={{ p: 3, borderRadius: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h5" fontWeight="bold">
            <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
            Department Task Calendar - {currentUser.department}
          </Typography>
          <Box display="flex" gap={1}>
            <Button size="small" onClick={goToPreviousMonth} variant="outlined">Previous</Button>
            <Button size="small" onClick={goToToday} variant={currentMonth.toDateString() === new Date().toDateString() ? 'contained' : 'outlined'}>Today</Button>
            <Button size="small" onClick={goToNextMonth} variant="outlined">Next</Button>
          </Box>
        </Box>
        
        <Typography variant="h6" align="center" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          {format(currentMonth, 'MMMM yyyy')}
        </Typography>
        
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {weekDays.map(day => (
            <Grid item xs key={day}>
              <Typography align="center" variant="subtitle2" fontWeight="bold" sx={{ py: 1, bgcolor: 'primary.main', color: 'white', borderRadius: 1, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>
        
        <Grid container spacing={1}>
          {generateCalendarDays.map((day, index) => {
            const hasEventsOnDay = day.events.length > 0;
            const isToday = day.isToday;
            const isCurrentMonth = day.isCurrentMonth;
            
            return (
              <Grid item xs={12/7} key={index}>
                <CalendarDayCell 
                  isCurrentMonth={isCurrentMonth} 
                  hasEvents={hasEventsOnDay} 
                  isToday={isToday} 
                  onClick={() => hasEventsOnDay && handleDayClick(day.date, day.events)}
                >
                  <Box textAlign="center">
                    <Typography variant="body2" fontWeight={isCurrentMonth ? 'normal' : 'light'} sx={{ color: isCurrentMonth ? 'text.primary' : 'text.disabled', fontWeight: isToday ? 'bold' : 'normal' }}>
                      {format(day.date, 'd')}
                    </Typography>
                  </Box>
                  {hasEventsOnDay && (
                    <Box sx={{ mt: 1 }}>
                      {day.events.slice(0, 2).map((event, idx) => (
                        <Tooltip key={idx} title={event.extendedProps?.full_title || event.title}>
                          <Chip 
                            size="small" 
                            label={event.title.length > 15 ? event.title.substring(0, 12) + '...' : event.title} 
                            sx={{ width: '100%', mb: 0.5, fontSize: '0.65rem', height: 20, bgcolor: event.color || '#757575', color: '#fff', '& .MuiChip-label': { px: 0.5 } }} 
                          />
                        </Tooltip>
                      ))}
                      {day.events.length > 2 && (
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem', display: 'block', textAlign: 'center' }}>
                          +{day.events.length - 2} more
                        </Typography>
                      )}
                    </Box>
                  )}
                </CalendarDayCell>
              </Grid>
            );
          })}
        </Grid>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 12, height: 12, bgcolor: '#4caf50', borderRadius: 1 }} /><Typography variant="caption">Completed</Typography></Box>
          <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 12, height: 12, bgcolor: '#ff9800', borderRadius: 1 }} /><Typography variant="caption">Pending</Typography></Box>
          <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 12, height: 12, bgcolor: '#2196f3', borderRadius: 1 }} /><Typography variant="caption">In Progress</Typography></Box>
          <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 12, height: 12, bgcolor: '#f44336', borderRadius: 1 }} /><Typography variant="caption">Overdue</Typography></Box>
          <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 12, height: 12, bgcolor: '#e0e0e0', border: '2px solid #2196f3' }} /><Typography variant="caption">Today</Typography></Box>
        </Box>
        
        {!hasEvents && (
          <Typography color="textSecondary" align="center" sx={{ mt: 4, py: 4 }}>
            <EventNote sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <br />
            No tasks scheduled for {format(currentMonth, 'MMMM yyyy')}
          </Typography>
        )}
      </Paper>
    );
  };
  
  // Dialog render functions
  const renderCalendarEventDialog = () => (
    <Dialog open={calendarEventDialogOpen} onClose={() => setCalendarEventDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            <EventNote sx={{ mr: 1, verticalAlign: 'middle' }} />
            Tasks for {selectedCalendarDate ? format(selectedCalendarDate, 'MMMM dd, yyyy') : ''}
          </Typography>
          <IconButton onClick={() => setCalendarEventDialogOpen(false)}><Close /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedDayEvents.length === 0 ? (
          <Typography color="textSecondary" align="center" py={4}>No tasks scheduled for this day</Typography>
        ) : (
          <List>
            {selectedDayEvents.map((event, index) => (
              <ListItem key={index} divider>
                <ListItemAvatar><Avatar sx={{ bgcolor: event.color || '#757575' }}><TaskAlt /></Avatar></ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="subtitle1" fontWeight="bold">{event.extendedProps?.full_title || event.title}</Typography>
                      <PriorityBadge label={event.extendedProps?.priority_text || 'MEDIUM'} priority={event.extendedProps?.priority || 'medium'} size="small" />
                      <StatusChip label={event.extendedProps?.status?.replace('_', ' ').toUpperCase() || 'PENDING'} status={event.extendedProps?.status || 'pending'} size="small" />
                    </Box>
                  }
                  secondary={
                    <Box mt={1}>
                      <Typography variant="body2" color="textSecondary"><strong>Staff:</strong> {event.extendedProps?.staff_name || 'Unassigned'}</Typography>
                      <Typography variant="body2" color="textSecondary"><strong>Completion:</strong> {event.extendedProps?.completion_percentage || 0}%</Typography>
                      <LinearProgress variant="determinate" value={event.extendedProps?.completion_percentage || 0} sx={{ height: 4, borderRadius: 2, mt: 1, width: '60%' }} />
                    </Box>
                  }
                />
                <Button variant="outlined" size="small" onClick={() => { setCalendarEventDialogOpen(false); viewTaskDetails({ id: event.id }, 'staff'); }}>View Details</Button>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions><Button onClick={() => setCalendarEventDialogOpen(false)}>Close</Button></DialogActions>
    </Dialog>
  );
  
  const renderCreateDeptTaskDialog = () => (
    <Dialog open={deptTaskDialogOpen} onClose={() => setDeptTaskDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle><Typography variant="h6" fontWeight="bold"><Add sx={{ mr: 1, verticalAlign: 'middle' }} />Create Department Task</Typography></DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField 
              label="Department" 
              fullWidth 
              value={currentUser.department} 
              disabled 
              InputProps={{ startAdornment: <BusinessCenter sx={{ mr: 1, color: '#666' }} /> }}
            />
          </Grid>
          <Grid item xs={12}><TextField label="Task Title" fullWidth required value={deptTaskForm.task_title} onChange={(e) => setDeptTaskForm({ ...deptTaskForm, task_title: e.target.value })} /></Grid>
          <Grid item xs={12}><TextField label="Task Description" fullWidth multiline rows={3} value={deptTaskForm.task_description} onChange={(e) => setDeptTaskForm({ ...deptTaskForm, task_description: e.target.value })} /></Grid>
          <Grid item xs={12} sm={6}><FormControl fullWidth><InputLabel>Task Type</InputLabel><Select value={deptTaskForm.task_type} onChange={(e) => setDeptTaskForm({ ...deptTaskForm, task_type: e.target.value })} label="Task Type"><MenuItem value="weekly">Weekly</MenuItem><MenuItem value="monthly">Monthly</MenuItem><MenuItem value="quarterly">Quarterly</MenuItem><MenuItem value="custom">Custom</MenuItem></Select></FormControl></Grid>
          <Grid item xs={12} sm={6}><FormControl fullWidth><InputLabel>Priority</InputLabel><Select value={deptTaskForm.priority} onChange={(e) => setDeptTaskForm({ ...deptTaskForm, priority: e.target.value })} label="Priority"><MenuItem value="high">High</MenuItem><MenuItem value="medium">Medium</MenuItem><MenuItem value="low">Low</MenuItem></Select></FormControl></Grid>
          <Grid item xs={12} sm={6}><TextField label="Start Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={deptTaskForm.start_date} onChange={(e) => setDeptTaskForm({ ...deptTaskForm, start_date: e.target.value })} /></Grid>
          <Grid item xs={12} sm={6}><TextField label="End Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={deptTaskForm.end_date} onChange={(e) => setDeptTaskForm({ ...deptTaskForm, end_date: e.target.value })} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions><Button onClick={() => setDeptTaskDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleCreateDeptTask} startIcon={<Save />}>Create Task</Button></DialogActions>
    </Dialog>
  );
  
  const renderAssignStaffDialog = () => (
    <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle><Box display="flex" alignItems="center" gap={1}><PersonAdd color="primary" /><Typography variant="h6" fontWeight="bold">Assign Task to Staff</Typography></Box></DialogTitle>
      <DialogContent>
        <Box mb={2} p={2} bgcolor="#f5f5f5" borderRadius={2}>
          <Typography variant="subtitle2" color="textSecondary">Task to Assign:</Typography>
          <Typography variant="body1" fontWeight="bold">{assigningTask?.task_title}</Typography>
          <Typography variant="caption" color="textSecondary">{currentUser.department} department</Typography>
        </Box>
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Select Staff Member (Department: {currentUser.department}):</Typography>
        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {staffList.length === 0 ? (
            <Typography color="textSecondary" align="center" py={4}>No staff members found in your department</Typography>
          ) : (
            staffList.map((staff) => (
              <ListItemButton key={staff.phone} selected={selectedStaffForAssignment?.phone === staff.phone} onClick={() => setSelectedStaffForAssignment(staff)} sx={{ borderRadius: 2, mb: 1 }}>
                <ListItemAvatar><Avatar sx={{ bgcolor: 'primary.main' }}>{staff.name?.charAt(0) || 'S'}</Avatar></ListItemAvatar>
                <ListItemText 
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      {staff.name}
                      {staff.phone === currentUser.phone && (
                        <Chip label="You" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  } 
                  secondary={`${staff.department} • ${staff.phone}`} 
                />
                {selectedStaffForAssignment?.phone === staff.phone && <CheckCircle color="success" />}
              </ListItemButton>
            ))
          )}
        </List>
      </DialogContent>
      <DialogActions><Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleAssignTaskToStaff} disabled={!selectedStaffForAssignment} startIcon={<HowToReg />}>Assign Task</Button></DialogActions>
    </Dialog>
  );
  
  const renderDailyUpdateDialog = () => (
    <Dialog open={dailyUpdateDialogOpen} onClose={() => setDailyUpdateDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle><Typography variant="h6" fontWeight="bold"><Update sx={{ mr: 1, verticalAlign: 'middle' }} />Add Daily Update</Typography></DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Task: <strong>{selectedStaffTask?.task_title || 'Selected Task'}</strong></Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}><TextField label="Update Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={dailyUpdateForm.update_date} onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, update_date: e.target.value })} /></Grid>
          <Grid item xs={12} sm={6}><TextField label="Hours Spent" type="number" fullWidth InputProps={{ inputProps: { min: 0, max: 24, step: 0.5 } }} value={dailyUpdateForm.hours_spent} onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, hours_spent: parseFloat(e.target.value) || 0 })} /></Grid>
          <Grid item xs={12} sm={6}><TextField label="Completion Percentage" type="number" fullWidth InputProps={{ inputProps: { min: 0, max: 100 } }} value={dailyUpdateForm.completion_percentage} onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, completion_percentage: parseInt(e.target.value) || 0 })} /></Grid>
          <Grid item xs={12} sm={6}><FormControl fullWidth><InputLabel>Status</InputLabel><Select value={dailyUpdateForm.status} onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, status: e.target.value })} label="Status"><MenuItem value="on_track">On Track</MenuItem><MenuItem value="blocked">Blocked</MenuItem><MenuItem value="completed">Completed</MenuItem></Select></FormControl></Grid>
          <Grid item xs={12}><TextField label="Progress Description *" fullWidth multiline rows={3} required value={dailyUpdateForm.progress_description} onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, progress_description: e.target.value })} placeholder="What did you accomplish today?" error={dailyUpdateForm.progress_description === ''} helperText={dailyUpdateForm.progress_description === '' ? 'Progress description is required' : ''} /></Grid>
          <Grid item xs={12}><TextField label="Challenges Faced" fullWidth multiline rows={2} value={dailyUpdateForm.challenges} onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, challenges: e.target.value })} placeholder="Any blockers or difficulties?" /></Grid>
          <Grid item xs={12}><TextField label="Next Steps" fullWidth multiline rows={2} value={dailyUpdateForm.next_steps} onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, next_steps: e.target.value })} placeholder="What are your plans for tomorrow?" /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions><Button onClick={() => setDailyUpdateDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleAddDailyUpdate} startIcon={<Send />} disabled={!dailyUpdateForm.progress_description || dailyUpdateForm.progress_description.trim() === ''}>Submit Update</Button></DialogActions>
    </Dialog>
  );
  
  const renderCommentDialog = () => (
    <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle><Typography variant="h6" fontWeight="bold"><InsertComment sx={{ mr: 1, verticalAlign: 'middle' }} />Add Comment</Typography></DialogTitle>
      <DialogContent><TextField label="Comment" fullWidth multiline rows={4} margin="normal" value={commentForm.comment} onChange={(e) => setCommentForm({ ...commentForm, comment: e.target.value })} placeholder="Write your comment here..." /></DialogContent>
      <DialogActions><Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleAddComment} startIcon={<InsertComment />}>Add Comment</Button></DialogActions>
    </Dialog>
  );
  
  const renderNotificationsDrawer = () => (
    <Dialog open={notificationDrawerOpen} onClose={() => setNotificationDrawerOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle><Box display="flex" justifyContent="space-between" alignItems="center"><Typography variant="h6" fontWeight="bold"><Notifications sx={{ mr: 1, verticalAlign: 'middle' }} />Notifications{unreadCount > 0 && <Badge badgeContent={unreadCount} color="error" sx={{ ml: 1 }} />}</Typography>{unreadCount > 0 && (<Button size="small" onClick={markAllNotificationsRead} startIcon={<DoneAll />}>Mark all as read</Button>)}</Box></DialogTitle>
      <DialogContent>
        {notifications.length === 0 ? (<Typography color="textSecondary" align="center" py={4}>No notifications</Typography>) : (notifications.map(notification => (<Card key={notification.id} sx={{ mb: 2, p: 2, bgcolor: notification.is_read ? 'transparent' : alpha('#2196f3', 0.1), cursor: 'pointer', transition: 'all 0.2s', '&:hover': { bgcolor: alpha('#2196f3', 0.05) } }} onClick={() => markNotificationRead(notification.id)}><Box display="flex" gap={2}><Box>{notification.notification_type === 'assigned' && <Assignment color="primary" />}{notification.notification_type === 'updated' && <Update color="info" />}{notification.notification_type === 'completed' && <CheckCircle color="success" />}{notification.notification_type === 'commented' && <Comment color="warning" />}</Box><Box flex={1}><Typography variant="body2" fontWeight="bold">{notification.title}</Typography><Typography variant="caption" color="textSecondary" display="block">{notification.message}</Typography><Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>{format(parseISO(notification.created_at), 'MMM dd, yyyy HH:mm')}</Typography></Box>{!notification.is_read && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2196f3' }} />}</Box></Card>)))}
      </DialogContent>
      <DialogActions><Button onClick={() => setNotificationDrawerOpen(false)}>Close</Button></DialogActions>
    </Dialog>
  );
  
  const renderTaskDetailsDialog = () => {
    if (!selectedTaskDetails) return null;
    const isStaffTask = !!selectedTaskDetails.phone;
    
    return (
      <Dialog open={taskDetailsDialogOpen} onClose={() => setTaskDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle><Box display="flex" justifyContent="space-between" alignItems="center"><Typography variant="h6" fontWeight="bold">Task Details</Typography><IconButton onClick={() => setTaskDetailsDialogOpen(false)}><Close /></IconButton></Box></DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>{selectedTaskDetails.task_title}</Typography>
                <Typography variant="body2" color="textSecondary">{selectedTaskDetails.task_description || 'No description provided'}</Typography>
                <Box mt={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Chip size="small" icon={<Person />} label={`Assigned by: ${selectedTaskDetails.assigned_by || 'System'}`} variant="outlined" />
                  <PriorityBadge label={selectedTaskDetails.priority?.toUpperCase()} priority={selectedTaskDetails.priority} size="small" />
                  <StatusChip label={selectedTaskDetails.status?.replace('_', ' ').toUpperCase()} status={selectedTaskDetails.status} size="small" />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Task Information</Typography>
                <Typography variant="body2" mb={1}><strong>Completion:</strong> {selectedTaskDetails.completion_percentage || 0}%</Typography>
                {isStaffTask ? (
                  <><Typography variant="body2" mb={1}><strong>Staff:</strong> {selectedTaskDetails.staff_name}</Typography><Typography variant="body2" mb={1}><strong>Phone:</strong> {selectedTaskDetails.phone}</Typography><Typography variant="body2" mb={1}><strong>Estimated Hours:</strong> {selectedTaskDetails.estimated_hours || 0}</Typography></>
                ) : (<Typography variant="body2" mb={1}><strong>Department:</strong> {selectedTaskDetails.department}</Typography>)}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Dates</Typography>
                {isStaffTask ? (
                  <><Typography variant="body2" mb={1}><strong>Scheduled Date:</strong> {format(parseISO(selectedTaskDetails.scheduled_date), 'MMMM dd, yyyy')}</Typography><Typography variant="body2" mb={1}><strong>Deadline:</strong> {format(parseISO(selectedTaskDetails.deadline), 'MMMM dd, yyyy HH:mm')}</Typography></>
                ) : (
                  <><Typography variant="body2" mb={1}><strong>Start Date:</strong> {format(parseISO(selectedTaskDetails.start_date), 'MMMM dd, yyyy')}</Typography><Typography variant="body2" mb={1}><strong>End Date:</strong> {format(parseISO(selectedTaskDetails.end_date), 'MMMM dd, yyyy')}</Typography></>
                )}
              </Paper>
            </Grid>
            {/* Display daily updates in task details */}
            {selectedTaskDetails.daily_updates && selectedTaskDetails.daily_updates.length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    <History sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: 16 }} />
                    Daily Update History
                  </Typography>
                  <List dense>
                    {selectedTaskDetails.daily_updates.slice(0, 5).map((update, idx) => (
                      <ListItem key={idx} divider>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                              <Typography variant="body2" fontWeight="bold">{format(parseISO(update.update_date), 'MMM dd, yyyy')}</Typography>
                              <Chip size="small" label={`${update.hours_spent || 0} hrs`} variant="outlined" />
                              <Chip size="small" label={update.status} variant="outlined" color={update.status === 'completed' ? 'success' : update.status === 'blocked' ? 'error' : 'info'} />
                              <Chip size="small" label={`${update.completion_percentage || 0}%`} variant="outlined" color="primary" />
                            </Box>
                          }
                          secondary={
                            <Box mt={1}>
                              <Typography variant="body2"><strong>Progress:</strong> {update.progress_description}</Typography>
                              {update.challenges && <Typography variant="body2" color="error.dark"><strong>Challenges:</strong> {update.challenges}</Typography>}
                              {update.next_steps && <Typography variant="body2" color="success.dark"><strong>Next Steps:</strong> {update.next_steps}</Typography>}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                    {selectedTaskDetails.daily_updates.length > 5 && (
                      <Typography variant="caption" color="textSecondary" align="center" display="block" sx={{ mt: 1 }}>
                        +{selectedTaskDetails.daily_updates.length - 5} more updates
                      </Typography>
                    )}
                  </List>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          {isStaffTask && selectedTaskDetails.status !== 'completed' && (<Button variant="contained" color="primary" onClick={() => { setTaskDetailsDialogOpen(false); setDailyUpdateForm({ ...dailyUpdateForm, staff_task_id: selectedTaskDetails.id, completion_percentage: selectedTaskDetails.completion_percentage || 0 }); setDailyUpdateDialogOpen(true); }} startIcon={<Update />}>Add Daily Update</Button>)}
          <Button onClick={() => setTaskDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };
  
  const renderConfirmCompleteDialog = () => (
    <Dialog open={confirmCompleteDialogOpen} onClose={() => setConfirmCompleteDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: '#fff3e0' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6" fontWeight="bold">Confirm Task Completion</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>Task: <strong>{pendingCompleteTask?.task_title}</strong></Typography>
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>This department task has {pendingCompleteTask?.total_staff} staff tasks assigned. Currently, only {pendingCompleteTask?.completed_staff} out of {pendingCompleteTask?.total_staff} are completed.</Alert>
          <Typography variant="body2" color="textSecondary" gutterBottom>Would you like to force mark this department task as complete? This will override the progress from individual staff tasks.</Typography>
          <FormControlLabel control={<Checkbox checked={forceComplete} onChange={(e) => setForceComplete(e.target.checked)} />} label="Yes, I want to force complete this task" sx={{ mt: 2 }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmCompleteDialogOpen(false)}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={handleForceCompleteConfirm} disabled={!forceComplete} startIcon={<CheckCircle />}>Force Complete</Button>
      </DialogActions>
    </Dialog>
  );
  
  // ==================== MAIN RENDER ====================
  
  if (initialLoading) {
    return (
      <Backdrop open={true} sx={{ zIndex: 9999 }}>
        <Box textAlign="center">
          <CircularProgress color="primary" />
          <Typography sx={{ mt: 2 }}>Loading HOD Dashboard...</Typography>
        </Box>
      </Backdrop>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f8f9fa' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, maxWidth: '1600px', mx: 'auto', width: '100%', overflow: 'auto' }}>
        <TopNav onLogout={() => { localStorage.clear(); window.location.href = '/login'; }} onNotificationClick={() => setNotificationDrawerOpen(true)} toggleSidebar={toggleSidebar} notificationCount={unreadCount} />
        
        {debugMode && (<Paper sx={{ p: 2, mb: 2, bgcolor: '#263238', color: '#fff', fontSize: '0.75rem', overflow: 'auto', maxHeight: 200 }}><Typography variant="caption" component="pre">{JSON.stringify(debugInfo, null, 2)}</Typography></Paper>)}
        
        <Fade in timeout={500}>
          <Box sx={{ mt: 4 }}>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Tab icon={<DashboardIcon />} label="Dashboard" />
              <Tab icon={<Assignment />} label="Department Tasks" />
              <Tab icon={<Group />} label="Staff Tasks" />
              <Tab icon={<TimelineIcon />} label="Analytics" />
              <Tab icon={<CalendarToday />} label="Calendar" />
            </Tabs>
            
            {activeTab === 0 && (
              <Box>
                {renderStatCards()}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 4 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom><Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />Recent Department Tasks</Typography>
                      <TableContainer><Table size="small"><TableHead><TableRow><TableCell>Task</TableCell><TableCell>Priority</TableCell><TableCell>Status</TableCell><TableCell>Progress</TableCell></TableRow></TableHead><TableBody>{departmentTasks.slice(0, 5).map((task) => (<TableRow key={task.id} hover><TableCell>{task.task_title}</TableCell><TableCell><PriorityBadge label={task.priority.toUpperCase()} priority={task.priority} size="small" /></TableCell><TableCell><StatusChip label={task.status.replace('_', ' ').toUpperCase()} status={task.status} size="small" /></TableCell><TableCell><LinearProgress variant="determinate" value={getDepartmentProgress(task)} sx={{ height: 4, borderRadius: 2, width: 80 }} /></TableCell></TableRow>))}</TableBody></Table></TableContainer>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 4 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom><Group sx={{ mr: 1, verticalAlign: 'middle' }} />Recent Staff Tasks</Typography>
                      <TableContainer><Table size="small"><TableHead><TableRow><TableCell>Staff</TableCell><TableCell>Task</TableCell><TableCell>Priority</TableCell><TableCell>Status</TableCell><TableCell>Updates</TableCell></TableRow></TableHead><TableBody>{staffTasks.slice(0, 5).map((task) => (<TableRow key={task.id} hover><TableCell>{task.staff_name}{task.phone === currentUser.phone && <Chip size="small" label="You" sx={{ ml: 1, height: 18 }} />}</TableCell><TableCell>{task.task_title}</TableCell><TableCell><PriorityBadge label={task.priority.toUpperCase()} priority={task.priority} size="small" /></TableCell><TableCell><StatusChip label={task.status.replace('_', ' ').toUpperCase()} status={task.status} size="small" /></TableCell><TableCell>{task.daily_update_count > 0 && <Chip size="small" label={`${task.daily_update_count} updates`} variant="outlined" size="small" />}</TableCell></TableRow>))}</TableBody></Table></TableContainer>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {activeTab === 1 && renderDepartmentTasksTable()}
            {activeTab === 2 && renderStaffTasksTable()}
            {activeTab === 3 && renderAnalyticsCharts()}
            {activeTab === 4 && renderCalendarView()}
          </Box>
        </Fade>
      </Box>
      
      <SpeedDial ariaLabel="Quick Actions" sx={{ position: 'fixed', bottom: 16, right: 16 }} icon={<SpeedDialIcon />}>
        {canCreateTasks() && (<SpeedDialAction icon={<Assignment />} tooltipTitle="Create Department Task" onClick={() => setDeptTaskDialogOpen(true)} />)}
        <SpeedDialAction icon={<TaskAlt />} tooltipTitle="Add Daily Update (My Tasks)" onClick={() => { 
          const myTasks = staffTasks.filter(t => t.phone === currentUser.phone && t.status !== 'completed');
          if (myTasks.length > 0) { 
            const task = myTasks[0];
            setSelectedStaffTask(task);
            setDailyUpdateForm({ 
              ...dailyUpdateForm, 
              staff_task_id: task.id, 
              completion_percentage: task.completion_percentage || 0 
            });
            setDailyUpdateDialogOpen(true);
          } else { 
            showSnackbar('No active tasks assigned to you to update', 'warning'); 
          } 
        }} />
        <SpeedDialAction icon={<Refresh />} tooltipTitle="Refresh All Data" onClick={refreshAllData} />
        <SpeedDialAction icon={debugMode ? <CheckCircle /> : <BugReport />} tooltipTitle={debugMode ? "Disable Debug" : "Enable Debug"} onClick={() => setDebugMode(!debugMode)} />
      </SpeedDial>
      
      {renderCreateDeptTaskDialog()}
      {renderAssignStaffDialog()}
      {renderDailyUpdateDialog()}
      {renderCommentDialog()}
      {renderTaskDetailsDialog()}
      {renderNotificationsDrawer()}
      {renderConfirmCompleteDialog()}
      {renderCalendarEventDialog()}
      {renderDailyUpdatesDialog()}
      
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TaskManagementHOD;